"use client";

import React, { useState, useEffect } from "react";
import { Menu, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const getRoute = (item: any) => {
    switch (item.label) {
      case 'Protocols':
        return "/Protocols";
      case 'Services':
        return "/Services";
      case 'M/O Members':
        return "/memberships";
      case 'M/O Memberships':
        return "/memberships";
      case 'M/O Collective':
        return "/MOCollective";
      case 'Podcast':
        return "/podcast";
      case 'Our Story':
        return "/OurStory";
      case 'Contact':
        return "/Contact";
      case 'Shop':
        return "/Shop";
      case 'Founders Notes':
        return "/FoundersNotes";
      default:
        return item.url || item.href || "/";
    }
  };
  if (loading) {
    return <div className="flex gap-2">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-9 w-20 bg-muted/30 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.filter(item => !(item.label === 'About M O' && (item.href === '/AboutMOPage' || item.url === '/AboutMOPage')) && item.label !== 'M/O Wellness Lab' && item.label !== 'Experiences').map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={getRoute(item)} className={cn(navigationMenuTriggerStyle(), "bg-transparent hover:bg-accent/30 focus:bg-accent/30 text-foreground font-default tracking-[0.3em] transition-all duration-300 relative after:absolute after:bottom-0 after:left-1/2 after:h-[1px] after:w-0 after:bg-foreground after:transition-all after:duration-300 hover:after:w-full hover:after:left-0")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  onClose
}: {
  onClose?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const getRoute = (item: any) => {
    switch (item.label) {
      case 'Protocols':
        return "/Protocols";
      case 'Services':
        return "/Services";
      case 'M/O Members':
        return "/memberships";
      case 'M/O Memberships':
        return "/memberships";
      case 'M/O Collective':
        return "/MOCollective";
      case 'Podcast':
        return "/podcast";
      case 'Our Story':
        return "/OurStory";
      case 'Contact':
        return "/Contact";
      case 'Shop':
        return "/Shop";
      case 'Founders Notes':
        return "/FoundersNotes";
      default:
        return item.url || item.href || "/";
    }
  };
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-muted/30 animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-4 mt-8">
      {menuItems.filter(item => !(item.label === 'About M O' && (item.href === '/AboutMOPage' || item.url === '/AboutMOPage')) && item.label !== 'M/O Wellness Lab' && item.label !== 'Experiences').map((item, i) => <Link key={item.id} to={getRoute(item)} onClick={onClose} className="text-lg font-default text-foreground hover:text-primary/70 transition-colors tracking-[0.3em]" data-index={i}>
          {item.label}
        </Link>)}
    </div>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="223238" id="header" className={cn("text-foreground sticky top-0 z-50 transition-all duration-500 border-b", isScrolled ? "bg-background/80 backdrop-blur-md border-border/50 shadow-sm" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="flex-shrink-0">
            <Link to="/" className="flex flex-col items-center justify-center">
              <WvcLogo className="h-7 w-auto" />
            </Link>
          </div>
        </div>

        <div className="hidden lg:flex flex-shrink-0 items-center justify-center">
          <MenuProvider menu_id="23">
            <DesktopNav />
          </MenuProvider>
        </div>

        <div className="flex items-center justify-end gap-2 flex-1">
          <div className="hidden md:block w-full max-w-[200px]">
            <ProductsSearch value={searchValue} onChange={setSearchValue} className="w-full [&_input]:bg-muted/20 [&_input]:border-border/40 [&_input]:text-foreground [&_input]:placeholder:text-muted-foreground [&_input]:font-default" />
          </div>
          <div className="md:hidden">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/30">
                  <Search className="size-5" />
                  <span className="sr-only">Search services</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-screen p-4 mt-2 bg-background border-border shadow-xl" align="end">
                <ProductsSearch value={searchValue} onChange={setSearchValue} className="w-full" />
              </PopoverContent>
            </Popover>
          </div>
          <CartIcon variant="ghost" size="icon" iconSize="size-5" className="text-foreground hover:bg-accent/30" />
          <Button asChild variant="outline" className="hidden xl:flex border-border hover:bg-accent/30 rounded-md font-default text-xs tracking-[0.2em] uppercase px-4 py-2 h-auto">
            <Link to="#">Shop Now</Link>
          </Button>
          <Button asChild className="hidden sm:flex bg-[#8FA88F] text-white hover:bg-[#8FA88F]/90 rounded-md font-default text-xs tracking-[0.2em] uppercase px-4 py-2 h-auto">
            <Link to="https://booking.mangomint.com/811506" target="_blank" rel="noopener noreferrer">Book Now</Link>
          </Button>
          <div className="lg:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/30">
                  <Menu className="size-6" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-background text-foreground border-r-border w-[300px]">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="mt-4">
                  <Link to="/" onClick={() => setIsOpen(false)} className="flex flex-col items-start">
                    <WvcLogo className="h-7 w-auto" />
                  </Link>
                </div>
                <MenuProvider menu_id="23">
                  <MobileNav onClose={() => setIsOpen(false)} />
                </MenuProvider>
                <div className="mt-8 flex flex-col gap-3">
                  <Button asChild variant="outline" className="w-full border-border hover:bg-accent/30 rounded-md font-default text-xs tracking-[0.2em] uppercase px-4 py-2 h-auto">
                    <Link to="#" onClick={() => setIsOpen(false)}>
                      Shop Now
                    </Link>
                  </Button>
                  <Button asChild className="w-full bg-[#8FA88F] text-white hover:bg-[#8FA88F]/90 rounded-md font-default text-xs tracking-[0.2em] uppercase px-4 py-2 h-auto">
                    <Link to="https://booking.mangomint.com/811506" target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)}>
                      Book Now
                    </Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}